<template>
  <div class="w-full mb-8 shadow bg-gray-50 rounded">
    <div class="p-4">
      <div class="flex justify-between items-center">
        <h3 class="font-semibold">{{ type.toUpperCase() }}</h3>
        <ToggleBtn
          :status="
            formDataLocal['category_page']['attributes'][`abi_cat_desc_${type}`]
          "
          @update:status="handleUpdateStatus"
        />
      </div>
      <div
        v-if="categoryLoading[`${this.type}Loading`]"
        class="flex justify-center mt-6"
      >
        <div
          style="border-top-color: transparent"
          class="ml-5 w-8 h-8 border-4 border-gray-200 border-solid rounded-full animate-spin"
        ></div>
      </div>

      <form
        v-if="
          formDataLocal['category_page']['attributes'][
            `abi_cat_desc_${type}`
          ] === 'yes' && !categoryLoading[`${this.type}Loading`]
        "
      >
        <h6
          v-if="basicAttributes ? Object.keys(basicAttributes).length : null"
          class="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase"
        >
          Basic
        </h6>
        <div class="flex flex-wrap">
          <!-- when type is faqs, render the extra attributes -->
          <div
            v-for="(value, name) in basicAttributes"
            :key="name"
            class="w-full lg:w-4/12 px-4"
          >
            <div v-if="value.type === 'number'" class="relative w-full mb-3">
              <label
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="formatAttrName(name)"
                  :description="value.description"
                />
              </label>
              <input
                type="number"
                autocomplete="off"
                data-lpignore="true"
                v-model="formDataLocal[`${type}_ctb`]['attributes'][name]"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
            </div>
            <div
              v-if="value.type === 'short_text'"
              class="relative w-full mb-3"
            >
              <label
                :title="value.description"
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="formatAttrName(name)"
                  :description="value.description"
                />
              </label>
              <input
                v-model="formDataLocal[`${type}_ctb`]['attributes'][name]"
                type="text"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
            </div>
            <div
              v-else-if="value.type === 'select' || value.type === 'boolean'"
              class="relative w-full mb-3"
            >
              <label
                :title="value.description"
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="formatAttrName(name)"
                  :description="value.description"
                />
              </label>
              <select
                v-model="formDataLocal[`${type}_ctb`]['attributes'][name]"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              >
                <option
                  v-for="(option, index) in changeOptionsFormat(value.options)"
                  :key="index"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div v-else-if="value.type === 'url'" class="relative w-full mb-3">
              <label
                :title="value.description"
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="formatAttrName(name)"
                  :description="value.description"
                />
              </label>
              <URLInput
                @update:url="(val) => handleUpdateValue(val, name)"
                :url="
                  formDataLocal[`${type}_ctb`]['attributes'][name]
                    ? formDataLocal[`${type}_ctb`]['attributes'][name]
                    : ''
                "
              />
            </div>
            <div v-else-if="value.type === 'icon'" class="relative w-full mb-3">
              <label
                :title="value.description"
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="formatAttrName(name)"
                  :description="value.description"
                />
              </label>
              <IconPicker
                :icons="value.options"
                :icon="
                  formDataLocal[`${type}_ctb`]['attributes'][name]
                    ? formDataLocal[`${type}_ctb`]['attributes'][name]
                    : ''
                "
                @update:icon="(val) => handleUpdateValue(val, name)"
              />
            </div>
            <div v-else class="relative w-full mb-3">
              <label
                :title="value.description"
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="formatAttrName(name)"
                  :description="value.description"
                />
              </label>
              <input
                v-model="formDataLocal[`${type}_ctb`]['attributes'][name]"
                type="text"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
            </div>
          </div>
        </div>
        <hr
          v-if="
            basicAttributes &&
            Object.keys(basicAttributes).length &&
            additionalAttributes &&
            Object.keys(additionalAttributes).length
          "
          class="mt-6 border-b-1 border-blueGray-300"
        />
        <h6
          v-if="
            additionalAttributes
              ? Object.keys(additionalAttributes).length
              : null
          "
          class="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase"
        >
          Additional
        </h6>
        <div class="flex flex-wrap">
          <div
            v-for="(value, name) in additionalAttributes"
            :key="name"
            class="w-full lg:w-12/12 px-4"
          >
            <div v-if="value.type === 'long_text'" class="relative w-full mb-3">
              <label
                :title="value.description"
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="formatAttrName(name)"
                  :description="value.description"
                />
              </label>
              <textarea
                v-if="type === 'faqs'"
                disabled
                type="text"
                class="h-40 border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                v-model="formDataLocal[`${type}_ctb`]['attributes'][name]"
              />
              <ckeditor
                v-else
                class="h-40 border-0 px-3 py-3 placeholder-blueGray-300 bg-white rounded shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                :editor="editor"
                :config="editorConfig"
                v-model="formDataLocal[`${type}_ctb`]['attributes'][name]"
              />
            </div>
          </div>
        </div>
        <hr
          v-if="type === 'faqs'"
          class="mt-6 border-b-1 border-blueGray-300"
        />
        <h6
          v-if="type === 'faqs'"
          class="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase"
        >
          Category FAQ Settings
        </h6>
        <div v-if="type === 'faqs'" class="flex flex-wrap">
          <!-- when type is faqs, render the extra attributes -->
          <div
            v-for="(value, name) in categoryFaqBasicAttributes"
            :key="name"
            class="w-full lg:w-4/12 px-4"
          >
            <div v-if="value.type === 'number'" class="relative w-full mb-4">
              <label
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="formatAttrName(name)"
                  :description="value.description"
                />
              </label>
              <input
                type="number"
                autocomplete="off"
                data-lpignore="true"
                v-model="formDataLocal['category_faq']['attributes'][name]"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
            </div>
          </div>
        </div>
        <div v-if="type === 'faqs'" class="flex flex-wrap">
          <div
            class="w-full"
            v-if="Object.keys(categoryFaqDynamicAttributes).length"
          >
            <DNElement
              v-for="index in itemNumber || 1"
              :key="index"
              :index="String(index)"
              :elementName="`faq`"
              :componentId="getComponentId('abi_category_faq')"
              :formData="formData['category_faq']['attributes']"
              :dynamicAttributes="categoryFaqDynamicAttributes"
              @update:formData="handleUpdateFormData"
            />
          </div>
          <div v-else class="w-full flex justify-center mt-6">
            <div
              style="border-top-color: transparent"
              class="ml-5 w-8 h-8 border-4 border-gray-200 border-solid rounded-full animate-spin"
            ></div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as Actions from "@/store/actions.type";
import * as Constants from "@/store/constants";
import CKEditor from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "@/assets/ckeditor/build/ckeditor";
import URLInput from "@/components/Shared/Input/URLInput";
import IconPicker from "@/components/Shared/Input/IconPicker";
import ToggleBtn from "@/components/Shared/Input/ToggleBtn";
import DNElement from "@/components/ComponentModule/DynamicNumComponent/DNElement";
import AttrTitleWithDescription from "@/components/Shared/Headers/AttrTitleWithDescription";

export default {
  name: "CategoryContentTextBlock",
  components: {
    ckeditor: CKEditor.component,
    URLInput,
    IconPicker,
    ToggleBtn,
    DNElement,
    AttrTitleWithDescription
  },
  props: {
    componentName: {
      type: String,
      required: true
    },
    formData: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    categoryName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      editor: ClassicEditor,
      editorConfig: Constants.CK_EDITOR_CONFIG
    };
  },
  computed: {
    itemNumber: {
      get: function () {
        const itemNumAttrName = Constants.FAQ_ATTRIBUTES.basic_attributes.find(
          (item) => item.includes("cols")
        );
        return parseInt(
          this.formDataLocal["category_faq"]["attributes"][itemNumAttrName]
        );
      },
      set: function (val) {
        const itemNumAttrName = Constants.FAQ_ATTRIBUTES.basic_attributes.find(
          (item) => item.includes("cols")
        );
        this.formDataLocal["category_faq"]["attributes"][itemNumAttrName] =
          parseInt(val);
      }
    },
    categoryFaqBasicAttributes() {
      const allAttributes =
        this.categoryAllAttributes[this.getComponentId("abi_category_faq")];
      let result = {};
      if (allAttributes) {
        const attrNames = Object.keys(allAttributes).filter((item) => {
          return Constants.FAQ_ATTRIBUTES.basic_attributes.includes(item);
        });
        attrNames.forEach((item) => {
          result[item] = allAttributes[item];
        });
        return result;
      } else {
        return {};
      }
    },
    categoryFaqDynamicAttributes() {
      const allAttributes =
        this.categoryAllAttributes[this.getComponentId("abi_category_faq")];
      let result = {};
      if (allAttributes) {
        Constants.FAQ_ATTRIBUTES.dynamic_attributes.forEach((item) => {
          Object.keys(allAttributes).forEach((i) => {
            if (i.includes(item)) {
              result[item] = allAttributes[i];
            }
          });
        });
        return result;
      } else {
        return {};
      }
    },
    categoryFaqAttr() {
      return this.categoryAllAttributes[
        this.getComponentId("abi_category_faq")
      ];
    },
    formDataLocal: {
      get: function () {
        return this.formData;
      },
      set: function (val) {
        this.$emit("update:formData", val);
      }
    },
    attributes() {
      return this.categoryAllAttributes[this.componentId];
    },
    componentId() {
      if (
        this.getAllComponent.find((item) => item.name === this.componentName)
      ) {
        return this.getAllComponent.find(
          (item) => item.name === this.componentName
        ).id;
      } else {
        return null;
      }
    },
    basicAttributes() {
      let formatObject = {};
      let attrRemoved = [
        "abi_ctb_text2_is_shortcode",
        "abi_ctb_text2",
        "abi_ctb_flex_is_column"
      ];
      const attributes = this.categoryAllAttributes[this.componentId];
      if (attributes) {
        formatObject = JSON.parse(JSON.stringify(attributes));
        for (const item in formatObject) {
          // remove all long_text type
          if (formatObject[item].type === "long_text") {
            delete formatObject[item];
          }
          attrRemoved.forEach((item) => {
            delete formatObject[item];
          });
        }
      }
      return formatObject;
    },
    additionalAttributes() {
      let formatObject = {};
      let attrRemoved =
        this.type === "faqs"
          ? [
              "abi_ctb_text2_is_shortcode",
              "abi_ctb_text2",
              "abi_ctb_flex_is_column",
              "abi_ctb_text1"
            ]
          : [
              "abi_ctb_text2_is_shortcode",
              "abi_ctb_text2",
              "abi_ctb_flex_is_column"
            ];
      const attributes = this.categoryAllAttributes[this.componentId];
      if (attributes) {
        formatObject = JSON.parse(JSON.stringify(attributes));
        for (const item in formatObject) {
          // only leave long_text type
          if (formatObject[item].type !== "long_text") {
            delete formatObject[item];
          }
          attrRemoved.forEach((item) => {
            delete formatObject[item];
          });
        }
      }
      return formatObject;
    },
    ...mapGetters([
      "getAllComponent",
      "categoryAllAttributes",
      "operationMode",
      "categoryLoading",
      "getRegion"
    ])
  },
  mounted() {
    this.itemNumber = 1;
    // fetch the attributes
    if (this.componentId && !this.categoryAllAttributes[this.componentName]) {
      this.$store.dispatch(Actions.SET_CATEGORY_LOADING_STATUS, {
        type: `${this.type}Loading`,
        status: true
      });
      this.$store
        .dispatch(Actions.FETCH_CATEGORY_ATTRIBUTES, {
          region: this.getRegion,
          componentId: this.componentId
        })
        .then(() => {
          if (this.type === "faqs" && this.categoryName) {
            this.setFaqsCtbDefaultValue();
            // fetch abi category faq attributes when type equals to faqs
            this.$store.dispatch(Actions.FETCH_CATEGORY_ATTRIBUTES, {
              region: this.getRegion,
              componentId: this.getComponentId("abi_category_faq")
            });
          } else {
            this.setDefaultTitle(this.type);
          }
          this.$store.dispatch(Actions.SET_CATEGORY_LOADING_STATUS, {
            type: `${this.type}Loading`,
            status: false
          });
        });
    }
  },
  watch: {
    itemNumber() {
      if (this.itemNumber > 0) {
        const faqNum =
          (Object.keys(this.formDataLocal["category_faq"]["attributes"] || {})
            .length -
            1) /
          2;
        const faqData = this.formDataLocal["category_faq"]["attributes"];
        for (let i = 0; i < this.itemNumber; i++) {
          if (
            // create empty attributes if attribute not exist
            !faqData[`abi_cf_question${i + 1}`] &&
            !faqData[`abi_cf_answer${i + 1}`]
          ) {
            faqData[`abi_cf_question${i + 1}`] = "";
            faqData[`abi_cf_answer${i + 1}`] = "";
          }
        }
        for (let i = this.itemNumber; i < faqNum; i++) {
          if (
            `abi_cf_question${i + 1}` in faqData &&
            `abi_cf_answer${i + 1}` in faqData
          ) {
            delete faqData[`abi_cf_question${i + 1}`];
            delete faqData[`abi_cf_answer${i + 1}`];
          }
        }
        faqData[`abi_cf_cols`] = this.itemNumber;
      }
      // set the max item number to 10
      if (this.itemNumber > 20) {
        this.itemNumber = 20;
      }

      // set the min item number to 1
      if (this.itemNumber <= 0) {
        this.itemNumber = 1;
      }
    },
    categoryName() {
      // set default value for create page
      if (this.operationMode === "create") {
        if (this.type === "faqs" && this.categoryName) {
          this.setFaqsCtbDefaultValue();
        } else {
          this.setDefaultTitle(this.type);
        }
      }
    },
    attributes() {
      // set default value for attributes form
      if (this.attributes) {
        Object.keys(this.attributes).forEach((key) => {
          if (!this.formDataLocal[`${this.type}_ctb`]["attributes"][key]) {
            this.formDataLocal[`${this.type}_ctb`]["attributes"][key] =
              this.categoryAllAttributes[this.componentId][key].value || "";
          }
        });
      }
    },
    componentId() {
      if (this.componentId) {
        this.$store.dispatch(Actions.FETCH_CATEGORY_ATTRIBUTES, {
          region: this.getRegion,
          componentId: this.componentId
        });
      }
    }
  },
  methods: {
    formatAttrName(name) {
      const nameArray = name.split("_");
      nameArray.splice(0, 2);
      return nameArray.join(" ");
    },
    changeOptionsFormat(options) {
      const newOptions = options.map((item) => {
        return { label: item, value: item };
      });
      return newOptions;
    },
    handleUpdateValue(val, name) {
      this.formDataLocal[`${this.type}_ctb`][name] = val;
    },
    handleUpdateFaqs(val) {
      this.formDataLocal = val;
    },
    handleUpdateStatus(val) {
      this.formDataLocal["category_page"]["attributes"][
        `abi_cat_desc_${this.type}`
      ] = val;
    },
    getComponentId(componentName) {
      if (this.getAllComponent.find((item) => item.name === componentName)) {
        return this.getAllComponent.find((item) => item.name === componentName)
          .id;
      } else {
        return null;
      }
    },
    setFaqsCtbDefaultValue() {
      const cat_name = this.categoryName.replaceAll("-", " ");
      const faq_attr = `${this.getComponentId("abi_category_faq")}|${
        this.categoryName
      }`;
      let faq_title = cat_name
        .split(" ")
        .map((item) => {
          return item.charAt(0).toUpperCase() + item.slice(1);
        })
        .join(" ");

      /*
         set default value for faqs data
         (abi_ctb_text1, abi_ctb_text1_is_shortcode, abi_ctb_title)
      */
      this.formDataLocal["faqs_ctb"]["attributes"][
        "abi_ctb_text1"
      ] = `abi_category_faq name=${faq_attr}`;
      this.formDataLocal["faqs_ctb"]["attributes"][
        "abi_ctb_text1_is_shortcode"
      ] = "yes";
      this.formDataLocal["faqs_ctb"]["attributes"][
        "abi_ctb_title"
      ] = `${faq_title} FAQs`;
    },
    setDefaultTitle(type) {
      const cat_name = this.categoryName.replaceAll("-", " ");
      let cat_title = cat_name
        .split(" ")
        .map((item) => {
          return item.charAt(0).toUpperCase() + item.slice(1);
        })
        .join(" ");
      if (type === "assistance") {
        this.formDataLocal[`${type}_ctb`]["attributes"]["abi_ctb_title"] = `${
          type.charAt(0).toUpperCase() + type.slice(1)
        } with choosing ${cat_title}`;
      } else if (type === "range") {
        this.formDataLocal[`${type}_ctb`]["attributes"][
          "abi_ctb_title"
        ] = `Our ${cat_title} ${type.charAt(0).toUpperCase() + type.slice(1)}`;
      } else if (type === "overview") {
        this.formDataLocal[`${type}_ctb`]["attributes"][
          "abi_ctb_title"
        ] = `Buy ${cat_title} Online`;
      }
    },
    handleUpdateFormData(val) {
      this.formDataLocal["catagory_faq"]["attribute"] = val;
    }
  }
};
</script>
